import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TranslationCacheService } from './translation-cache.service';

/**
 * RouteTranslationResolver - Automatic page translations loading with caching
 *
 * Purpose:
 * - Automatically loads page-specific translations before component renders
 * - Uses centralized TranslationCacheService to prevent duplicate requests
 * - Works with PAGES_SUBMODULES structure (pages/ subdirectory)
 * - Merges loaded translations into TranslateService
 *
 * Usage:
 * Add to route configuration:
 * ```typescript
 * {
 *   path: '',
 *   resolve: { translations: RouteTranslationResolver },
 *   loadComponent: () => import('./component')
 * }
 * ```
 *
 * How it works:
 * 1. Extracts page key from route path
 * 2. Checks TranslationCacheService for cached translations
 * 3. If not cached: loads from assets/i18n/{lang}/pages/{pageKey}.json
 * 4. Stores in cache and merges into TranslateService with pages.{pageKey} namespace
 * 5. Component receives fully loaded translations on init
 */
@Injectable({
  providedIn: 'root'
})
export class RouteTranslationResolver implements Resolve<any> {

  constructor(
    private translateService: TranslateService,
    private http: HttpClient,
    private cache: TranslationCacheService
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const pageKey = this.extractPageKey(route, state);

    if (!pageKey) {
      return of(null);
    }

    // Namespace key can differ from file name (e.g., file=tariff-coefficients, namespace=tariffCoefficients)
    const namespaceKey = (route.data && route.data['translationNamespace']) || pageKey;

    const currentLang = this.translateService.currentLang || 'en';

    // Load primary translation file
    const primary$ = this.loadTranslationFile(currentLang, pageKey, namespaceKey);

    // Load additional translation files if specified in route data
    // Usage: data: { additionalTranslations: ['users'] }
    const additionalKeys: string[] = (route.data && route.data['additionalTranslations']) || [];
    if (additionalKeys.length > 0) {
      const all$ = [primary$, ...additionalKeys.map(key => this.loadTranslationFile(currentLang, key, key))];
      return forkJoin(all$);
    }

    return primary$;
  }

  /**
   * Load a single translation file by pageKey, wrap in namespace and merge
   */
  private loadTranslationFile(lang: string, pageKey: string, namespaceKey: string): Observable<any> {
    const cacheKey = `${lang}_page_${pageKey}`;

    // Check cache first
    const cached$ = this.cache.get(cacheKey);
    if (cached$) {
      // CRITICAL: Apply merge even for cached translations
      return cached$.pipe(
        tap(translations => {
          this.mergeTranslations(lang, translations);
        })
      );
    }

    // Load from file if not cached
    const path = `./assets/i18n/${lang}/pages/${pageKey}.json`;

    const request$ = this.http.get(path).pipe(
      map((pageTranslations: any) => {
        // Wrap in pages namespace: pages.drivers.*, pages.tariffCoefficients.*
        return {
          pages: {
            [namespaceKey]: pageTranslations
          }
        };
      }),
      tap(translations => {
        this.mergeTranslations(lang, translations);
      }),
      catchError(error => {
        return of(null);
      })
    );

    // Store in cache
    this.cache.set(cacheKey, request$);

    return request$;
  }

  /**
   * Merge translations into TranslateService
   */
  private mergeTranslations(lang: string, translations: any): void {
    if (!translations || Object.keys(translations).length === 0) {
      return;
    }

    // Use shouldMerge=true to preserve existing translations from other pages
    this.translateService.setTranslation(lang, translations, true);
  }

  /**
   * Flatten nested translations to dot notation
   * Example: { pages: { users: { title: 'Users' } } } -> { 'pages.users.title': 'Users' }
   */
  private flattenTranslations(obj: any, prefix: string = ''): any {
    const result: any = {};

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          Object.assign(result, this.flattenTranslations(obj[key], newKey));
        } else {
          result[newKey] = obj[key];
        }
      }
    }

    return result;
  }

  /**
   * Extract page key from route path
   * Examples:
   * - /companies -> 'companies'
   * - /companies/create -> 'companies'
   * - /users/123 -> 'users'
   * - /dispatch -> 'dispatch'
   */
  private extractPageKey(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): string | null {
    // Check if route has explicit translationKey in data
    if (route.data && route.data['translationKey']) {
      return route.data['translationKey'];
    }

    // Get full URL path
    const fullPath = state.url;

    // Extract the main route segment (first part after /)
    const pathSegments = fullPath.split('/').filter(segment => segment.length > 0);

    if (pathSegments.length === 0) {
      return null;
    }

    // Return the first segment as the page key
    // This handles parameterized routes automatically (e.g., /companies/123 -> 'companies')
    return pathSegments[0];
  }
}
